import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { Modal, View, Text, StyleSheet, Pressable } from 'react-native';
import { trackUIError } from '../../utils/analytics';

type ErrorContextType = {
  showError: (msg: string) => void;
};

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useGlobalError = () => {
  const ctx = useContext(ErrorContext);
  if (!ctx) throw new Error('useGlobalError must be inside GlobalErrorProvider');
  return ctx;
};

interface Props {
  children: ReactNode;
}

export function GlobalErrorProvider({ children }: Props) {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');

  // expose showError without needing navigate yet
  const showError = (msg: string) => {
    console.log('GlobalErrorProvider showError called with:', msg);
    
    // Log UI error
    trackUIError(msg, 'GlobalErrorProvider');
    
    setMessage(msg);
    setVisible(true);
  };

  return (
    <ErrorContext.Provider value={{ showError }}>
      {children}
      <ErrorModal
        visible={visible}
        message={message}
        onClose={() => setVisible(false)}
      />
    </ErrorContext.Provider>
  );
}

// separate component so useNavigate is only called inside BrowserRouter
function ErrorModal({
  visible,
  message,
  onClose,
}: {
  visible: boolean;
  message: string;
  onClose: () => void;
}) {
  const handleOk = () => {
    onClose();
  };

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.box}>
          <Text style={styles.title}>Error</Text>
          <Text style={styles.message} testID="errorModalMessage">
            {message}
          </Text>
          <Pressable style={styles.button} onPress={handleOk} testID="errorModalOkButton">
            <Text style={styles.buttonText}>OK</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  box: {
    width: '80%',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
  },
  title: { fontSize: 18, fontWeight: '600', marginBottom: 8 },
  message: { fontSize: 16, marginBottom: 20 },
  button: {
    alignSelf: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000',
    borderRadius: 8,
  },
  buttonText: { color: '#fff', fontWeight: '600' },
});
